import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog.js';
import { Button } from '@/components/ui/button.js';
import { Badge } from '@/components/ui/badge.js';
import { CheckCircle, XCircle, ExternalLink } from 'lucide-react';
import { ScrollArea } from '@/components/ui/scroll-area.js';
import type { PendingApproval } from '@/stores/approvalStore.js';
import { useServices } from '@/contexts/ServicesContext.js';
import { useNavigate } from 'react-router-dom';

interface ApprovalDetailModalProps {
  approval: PendingApproval;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onApprove: (e?: React.MouseEvent) => void;
  onReject: (e?: React.MouseEvent) => void;
  isProcessing: boolean;
}

export function ApprovalDetailModal({
  approval,
  open,
  onOpenChange,
  onApprove,
  onReject,
  isProcessing,
}: ApprovalDetailModalProps) {
  const navigate = useNavigate();
  const { sessionService } = useServices();

  const handleViewSession = async () => {
    try {
      await sessionService.switchSession(approval.sessionId);
      navigate(`/chat/${approval.sessionId}`);
      onOpenChange(false);
    } catch (error) {
      console.error('[ApprovalDetailModal] Failed to switch session:', error);
    }
  };

  const handleApprove = (e?: React.MouseEvent) => {
    if (e) {
      e.stopPropagation();
    }
    onApprove(e);
    onOpenChange(false);
  };

  const handleReject = (e?: React.MouseEvent) => {
    if (e) {
      e.stopPropagation();
    }
    onReject(e);
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Approval Request</DialogTitle>
          <DialogDescription>
            Review the details before approving or rejecting this request
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Tool Info */}
          <div>
            <label className="text-sm font-medium text-muted-foreground">Tool</label>
            <div className="mt-1">
              <Badge variant="outline" className="font-mono">
                {approval.toolName}
              </Badge>
            </div>
          </div>

          {/* Message */}
          {approval.message && (
            <div>
              <label className="text-sm font-medium text-muted-foreground">Message</label>
              <p className="mt-1 text-sm">{approval.message}</p>
            </div>
          )}

          {/* Session Info */}
          <div>
            <label className="text-sm font-medium text-muted-foreground">Session</label>
            <div className="mt-1 flex items-center gap-2">
              <code className="text-xs bg-muted px-2 py-1 rounded">
                {approval.sessionId.slice(0, 8)}...
              </code>
              <Button
                variant="ghost"
                size="sm"
                onClick={handleViewSession}
                className="h-7 text-xs"
              >
                <ExternalLink className="h-3 w-3 mr-1" />
                View session
              </Button>
            </div>
          </div>

          {/* Arguments */}
          <div>
            <label className="text-sm font-medium text-muted-foreground">Arguments</label>
            <ScrollArea className="mt-1 max-h-64 w-full rounded-md border">
              <pre className="p-3 text-xs">
                {JSON.stringify(approval.toolArgs, null, 2)}
              </pre>
            </ScrollArea>
          </div>

          {/* Timestamp */}
          <div className="text-xs text-muted-foreground">
            Requested {new Date(approval.timestamp).toLocaleString()}
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isProcessing}>
            Cancel
          </Button>
          <Button
            variant="destructive"
            onClick={handleReject}
            disabled={isProcessing}
            className="gap-2"
          >
            <XCircle className="h-4 w-4" />
            Reject
          </Button>
          <Button onClick={handleApprove} disabled={isProcessing} className="gap-2">
            <CheckCircle className="h-4 w-4" />
            Approve
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
